import { useEffect } from "react";

/**
 * Custom React hook that triggers a handler function when a click or touch event occurs outside the specified element.
 *
 * @template T - The type of the HTMLElement referenced.
 * @param ref - A React ref object pointing to the element to detect outside clicks for.
 * @param handler - A callback function to be invoked when a click or touch event occurs outside the referenced element.
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useClickOutside(ref, () => {
 *   // Handle outside click
 * });
 *
 * @remarks
 * This hook attaches event listeners for "mousedown" and "touchstart" events to the document.
 * The handler will not be called if the event occurs inside the referenced element or if the ref is not set.
 * Event listeners are cleaned up automatically when the component unmounts or dependencies change.
 */
export function useClickOutside<T extends HTMLElement>(
  ref: React.RefObject<T>,
  handler: (event: MouseEvent | TouchEvent) => void
) {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const el = ref.current;
      // Do nothing if clicking inside or ref not set
      if (!el || el.contains(event.target as Node)) return;
      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}
